const formatIDFromArtboard = (artboardID) => {
  const raw = String(artboardID || "").trim();
  const separator = "__";
  const index = raw.indexOf(separator);
  return index > 0 ? raw.slice(0, index) : raw;
};

export const getProduceSlidesPerGalleryItem = ({
  isCarousel = false,
  slides = [],
  slideCount,
} = {}) => {
  const explicit = Number(slideCount);
  if (Number.isInteger(explicit) && explicit >= 1) return explicit;
  if (!isCarousel) return 1;
  return Math.max(1, slides.length || 1);
};

export const countProduceGalleryGroups = ({
  count = 0,
  slidesPerItem = 1,
} = {}) => {
  const perItem = Math.max(1, Number(slidesPerItem) || 1);
  return Math.ceil(Math.max(0, Number(count) || 0) / perItem);
};

export const getProduceGalleryGroupKey = ({ item } = {}) => {
  const formatID = formatIDFromArtboard(item?.artboardID);
  return `${String(item?.jobID || "")}::${Number(
    item?.rowIndex ?? item?.productionRowIndex ?? 0
  )}::${formatID}`;
};

export const groupProduceCreativesAsGalleryItems = ({
  items = [],
  slidesPerItem = 1,
  hideIncompleteGroups = false,
} = {}) => {
  const perItem = Math.max(1, Number(slidesPerItem) || 1);
  if (perItem <= 1) return items;

  const groups = new Map();
  items.forEach((item) => {
    const key = getProduceGalleryGroupKey({ item });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  const result = [];
  groups.forEach((slides) => {
    const sorted = [...slides].sort(
      (left, right) => (left.slideIndex ?? 0) - (right.slideIndex ?? 0)
    );
    if (hideIncompleteGroups && sorted.length < perItem) return;
    if (sorted.length < 2) {
      result.push(...sorted);
      return;
    }
    const first = sorted[0];
    result.push({
      ...first,
      isCarousel: true,
      carouselImages: sorted.map((slide) => ({
        ...slide,
        url: slide.url,
        id: slide.id,
        width: slide.width,
        height: slide.height,
        rating: null,
      })),
      url: first.url,
    });
  });
  return result;
};

export const groupProduceFailedSlotsAsGalleryItems = ({
  slots = [],
  succeededItems = [],
  slidesPerItem = 1,
} = {}) => {
  const perItem = Math.max(1, Number(slidesPerItem) || 1);
  if (perItem <= 1) return slots;

  const succeededKeys = new Set(
    succeededItems.map((item) => getProduceGalleryGroupKey({ item }))
  );
  const groups = new Map();
  slots.forEach((slot) => {
    const key = getProduceGalleryGroupKey({ item: slot });
    if (succeededKeys.has(key)) return;
    if (!groups.has(key)) groups.set(key, slot);
  });
  return [...groups.values()];
};

export const countProducePendingGalleryItems = ({
  inFlight = 0,
  expectedCreativeCount = 0,
  completedCount = 0,
  isStartingProduce = false,
  slidesPerItem = 1,
} = {}) => {
  if (isStartingProduce) {
    return Math.max(
      countProduceGalleryGroups({ count: inFlight, slidesPerItem }),
      countProduceGalleryGroups({
        count: (Number(expectedCreativeCount) || 0) - completedCount,
        slidesPerItem,
      }),
      0
    );
  }
  return Math.max(
    countProduceGalleryGroups({ count: inFlight, slidesPerItem }),
    0
  );
};

export const resolveProduceGallerySlideItem = ({
  item,
  carouselIndex,
  url,
} = {}) => {
  if (!item) return null;
  const slides =
    item.isCarousel && Array.isArray(item.carouselImages)
      ? item.carouselImages
      : [];
  if (slides.length === 0) return item;

  const index = Number(carouselIndex);
  if (Number.isInteger(index) && index >= 0 && slides[index]?.url) {
    return slides[index];
  }

  const matchUrl = String(url || "").trim();
  if (matchUrl) {
    const byUrl = slides.find((slide) => slide?.url === matchUrl);
    if (byUrl) return byUrl;
  }

  return item;
};
